import React, { useState } from 'react';
import './Login.css';
import Spinner from './Spinner';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errorDetails, setErrorDetails] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [theme, setTheme] = useState(localStorage.getItem('phoenixTheme') || 'light');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setErrorDetails(null);
    setSubmitting(true);
    try {
      const baseEnv = process.env.REACT_APP_API_URL || '';
      const hostname = window.location.hostname;
      const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1';
      // Prefer same-origin (relative) requests when served from the production domain
      // This avoids mixed-content / CORS when nginx terminates TLS and serves the SPA.
      let base = '';
      try {
        if (isLocalHost) {
          base = '';
        } else if (baseEnv) {
          // If the configured API URL has same origin as the page, use relative path.
          const envUrl = new URL(baseEnv);
          if (envUrl.origin === window.location.origin) {
            base = '';
          } else {
            base = baseEnv.replace(/\/$/, '');
          }
        } else {
          base = '';
        }
      } catch (e) {
        // If parsing fails, fall back to using the env raw value (or relative)
        base = baseEnv ? baseEnv.replace(/\/$/, '') : '';
      }
      const url = base ? `${base}/api/login` : '/api/login';
      console.log('Submitting login to', url);

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'Accept': 'application/json' },
        credentials: 'include',
        body: `username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`
      });

      // ler corpo como texto e tentar interpretar como JSON
      const text = await res.text();
      let data: any = null;
      try { data = text ? JSON.parse(text) : null; } catch (e) { data = null; }

      // backend pode retornar { success: true } ou { ok: true }
      if (res.ok && data && (data.success === true || data.ok === true)) {
        // Confirm session is active by requesting /api/test-session (ensure cookie is present)
        try {
          const checkUrl = base ? `${base}/api/test-session` : '/api/test-session';
          const check = await fetch(checkUrl, { method: 'GET', credentials: 'include' });
          if (check.ok) {
            // hard navigate so AuthProvider will re-check and set authenticated state
            window.location.href = '/home';
            return;
          }
        } catch (e) {
          // ignore and fallback to reload
        }
        // fallback: reload the page to force AuthProvider to re-evaluate session
        window.location.reload();
        return;
      } else {
        const message = data && (data.error || data.message) ? (data.error || data.message) : (text || `${res.status} ${res.statusText}`);
        setError(message || 'Usuário ou senha inválidos');
        setErrorDetails(`Status: ${res.status} ${res.statusText}\nBody: ${text || '<empty>'}\nRequest: ${url}`);
      }
    } catch (err: any) {
      const msg = err && err.message ? err.message : String(err);
      const hint = msg.includes('Failed to fetch') ? 'Possíveis causas: backend inativo, problema de CORS ou mixed-content (HTTPS frontend tentando acessar HTTP backend). Verifique se o backend está rodando e a variável REACT_APP_API_URL.' : '';
      setError('Erro ao conectar ao servidor');
      setErrorDetails(`${msg}\n${hint}`);
      console.error('Login fetch error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    localStorage.setItem('phoenixTheme', newTheme);
    document.documentElement.setAttribute('data-bs-theme', newTheme);
  };

  React.useEffect(() => {
    document.documentElement.setAttribute('data-bs-theme', theme);
  }, [theme]);

  return (
    <main className="main" id="top">
      {submitting && <Spinner />}
      <div className="login-container">
        <div className="login-layout">
          <div className="login-video-panel" aria-hidden="true">
            <video
              className="login-video"
              src={theme === 'dark' ? '/assets/video/login_anim_dark_vid.mp4' : '/assets/video/login_anim_vid.mp4'}
              autoPlay
              loop
              muted
              playsInline
            />
          </div>
          <div className="card shadow-lg border-0 rounded-4 p-4 bg-body">
            <div className="d-flex flex-center text-decoration-none mb-4">
              <img src="/assets/img/icons/logo2.png" alt="TrustSis" width={250} />
            </div>
            <div className="d-flex justify-content-end mb-2">
              <button className="btn btn-sm btn-outline-secondary" type="button" onClick={toggleTheme}>
                <span className={`uil ${theme === 'light' ? 'uil-moon' : 'uil-sun'}`}></span>
              </button>
            </div>
            <div className="text-center mb-4">
              <h3 className="text-body-highlight">Entrar</h3>
              <p className="text-body-tertiary">Acesse sua conta TrustSis</p>
            </div>
            {error && (
              <div>
                <div className="alert alert-danger">{error}</div>
                {errorDetails && (
                  <pre className="small bg-light p-2" style={{whiteSpace: 'pre-wrap'}}>{errorDetails}</pre>
                )}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="username" className="form-label">Usuário</label>
                <input type="text" className="form-control" id="username" name="username" required autoFocus value={username} onChange={e => setUsername(e.target.value)} />
              </div>
              <div className="mb-3">
                <label htmlFor="password" className="form-label">Senha</label>
                <input type="password" className="form-control" id="password" name="password" required value={password} onChange={e => setPassword(e.target.value)} autoComplete="current-password" />
              </div>
              <button type="submit" className="btn btn-primary w-100">Entrar</button>
            </form>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Login;
